import PropTypes from 'prop-types';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { forwardRef, useEffect, useMemo } from 'react';
import {
	CascadeIcon,
	CompletedIcon,
	ControlIcon,
	CreateIcon,
	DefaultIcon,
	DraftIcon,
	DocumentIcon,
	InboxIcon,
	InProgressIcon,
	InsigitsIcon,
	ParticipatedIcon,
	ReportIcon,
	SundriesIcon,
	UpcomingIcon,
} from 'assets/icons/menu-icons';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, ListItemButton, ListItemIcon, ListItemText, Tooltip, Typography, useMediaQuery } from '@mui/material';

// project import
import { activeItem, openDrawer } from 'store/features/menu/menuSlice';

const normalizeMenuKey = (value = '') => value.toLowerCase().trim().replace(/\s+/g, '-');

const MENU_ICON_MAP = {
	upcoming: UpcomingIcon,
	completed: CompletedIcon,
	'in-progress': InProgressIcon,
	draft: DraftIcon,
	create: CreateIcon,
	inbox: InboxIcon,
	insights: InsigitsIcon,
	insigits: InsigitsIcon,
	participated: ParticipatedIcon,
	cascade: CascadeIcon,
	control: ControlIcon,
	controls: ControlIcon,
	document: DocumentIcon,
	documents: DocumentIcon,
	report: ReportIcon,
	reports: ReportIcon,
	sundries: SundriesIcon,
};

const getRouteMenuKey = (currentPathname) => {
	const pathSegments = currentPathname.split('/').filter(Boolean);

	if (pathSegments[0] === 'modules' && pathSegments[1]) {
		return pathSegments[2] ?? 'insights';
	}

	return pathSegments[0] ?? '';
};

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

const NavItem = ({ item, level, accessor, showIcon = true }) => {
	const theme = useTheme();
	const dispatch = useDispatch();
	const { pathname } = useLocation();
	const matchDownMD = useMediaQuery(theme.breakpoints.down('lg'));

	const drawerOpen = useSelector((state) => state.menu.drawerOpen);
	const openItem = useSelector((state) => state.menu.openItem);
	const menuNotification = useSelector((state) => state.menu.menuNotification);

	const activeBtnHandler = (id) => {
		// console.log('NavItem -> activeBtnHandler', id);
		// if (item?.parentId) {
		//   dispatch(activeItem({ openItem: [openItem[0], id] }));
		// } else {
		dispatch(activeItem({ openItem: [id] }));
		// }

		//matchDownMD && handleDrawerToggle();

		matchDownMD && dispatch(openDrawer({ drawerOpen: !drawerOpen }));
	};

	const pathName = normalizeMenuKey(item?.displayName ?? '');

	// if (item?.parentId && parentPath && item.displayName) {
	//   const childPath = item.displayName.toLowerCase().replace(" ", "-");
	//   pathName = `${parentPath}/${childPath}`;
	// } else if(item?.displayName) {
	//   pathName = item.displayName.toLowerCase().replace(" ", "-");
	// }
	// console.log(pathName);

	let listItemProps = { component: forwardRef((props, ref) => <Link ref={ref} {...props} to={pathName} />) };

	listItemProps.component.displayName = 'NavItemLink';

	const MenuItemIcon = useMemo(() => {
		const keys = [
			pathName,
			normalizeMenuKey(item?.displayName ?? ''),
			normalizeMenuKey(item?.name ?? ''),
			normalizeMenuKey(item?.url ?? ''),
		].filter(Boolean);

		const matchedKey = keys.find((key) => MENU_ICON_MAP[key]);
		return matchedKey ? MENU_ICON_MAP[matchedKey] : DefaultIcon;
	}, [item?.displayName, item?.name, item?.url, pathName]);

	const isSelected = openItem?.findIndex((id) => id === pathName) > -1;

	// Keep active drawer item synced with URL changes.
	useEffect(() => {
		const currentMenuKey = getRouteMenuKey(pathname);
		if (currentMenuKey === pathName) {
			dispatch(activeItem({ openItem: [pathName] }));
		}
	}, [dispatch, pathName, pathname]);

	const displayName = item?.[accessor]?.toLowerCase();
	const listCode = item?.name;
	const notification = displayName ? menuNotification?.[displayName] : null;
	const notificationCount = notification?.count ?? 0;
	const notificationStatus = notification?.status ?? null;
	const chipLabel = item?.chip?.label ?? null;

	// const resolvedSelectionVariant = selectionVariant ?? (level > 1 ? 'dark' : 'brand');
	// const isDarkSelection = resolvedSelectionVariant === 'dark';

	const selectedBg = '#0F172A';
	const selectedColor = '#FFFFFF';
	const defaultColor = '#334155';
	const hoverBg = '#F8FAFC';
	const isCompactTopLevel = !drawerOpen && level === 1;

	const content = (
		<ListItemButton
			{...listItemProps}
			disabled={item.disabled}
			onClick={() => activeBtnHandler(pathName)}
			selected={isSelected}
			sx={{
				borderRadius: '10px',
				px: isCompactTopLevel ? 1 : 1.5,
				py: 1,
				gap: 1,
				justifyContent: isCompactTopLevel ? 'center' : 'flex-start',
				color: defaultColor,
				'&:hover': { bgcolor: hoverBg },
				'&.Mui-selected': {
					bgcolor: selectedBg,
					color: selectedColor,
					'&:hover': { bgcolor: selectedBg },
				},
			}}
		>
			{showIcon && (
				<ListItemIcon
					sx={{
						minWidth: 28,
						justifyContent: 'center',
						mr: isCompactTopLevel ? 0 : 'auto',
						color: isSelected ? selectedColor : defaultColor,
					}}
				>
					<MenuItemIcon color={isSelected ? selectedColor : defaultColor} width="18px" height="18px" />
				</ListItemIcon>
			)}

			{(drawerOpen || (!drawerOpen && level !== 1)) && (
				<ListItemText
					sx={{
						my: 0,
					}}
					primary={
						<Typography
							component="span"
							sx={{
								fontSize: '0.875rem',
								fontWeight: isSelected ? 600 : 500,
								color: isSelected ? selectedColor : defaultColor,
							}}
						>
							{item?.[accessor]}
						</Typography>
					}
				/>
			)}

			{!isCompactTopLevel && (notificationCount > 0 || chipLabel || (displayName === 'inbox' && notificationStatus === 'NEW')) && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					{chipLabel && (
						<Box
							component="span"
							sx={{
								fontSize: '0.625rem',
								fontWeight: 600,
								lineHeight: 1.4,
								px: 1,
								py: '2px',
								borderRadius: '999px',
								bgcolor: isSelected ? 'rgba(255,255,255,0.1)' : '#F1F5F9',
								border: '1px solid',
								borderColor: isSelected ? 'rgba(255,255,255,0.16)' : '#E2E8F0',
								color: isSelected ? 'rgba(255,255,255,0.9)' : '#334155',
							}}
						>
							{chipLabel}
						</Box>
					)}

					{displayName === 'inbox' && notificationStatus === 'NEW' && (
						<Box
							component="span"
							sx={{
								fontSize: '0.625rem',
								fontWeight: 600,
								lineHeight: 1.4,
								px: 1,
								py: '2px',
								borderRadius: '999px',
								bgcolor: isSelected ? 'rgba(255,255,255,0.1)' : '#FFFFFF',
								border: '1px solid',
								borderColor: isSelected ? 'rgba(255,255,255,0.16)' : '#DBEAFE',
								color: isSelected ? 'rgba(255,255,255,0.9)' : 'var(--brand, #1D4ED8)',
							}}
						>
							New
						</Box>
					)}

					{notificationCount > 0 && (
						<Typography
							component="span"
							sx={{
								fontSize: '0.625rem',
								fontWeight: 600,
								color: isSelected ? 'rgba(255,255,255,0.8)' : '#64748B',
							}}
						>
							{notificationCount}
						</Typography>
					)}
				</Box>
			)}
		</ListItemButton>
	);

	if (listCode === 'lyn-process-delegation') {
		if (menuNotification['delegated items']?.count <= 0) return null;
	}

	return isCompactTopLevel ? (
		<Tooltip title={item?.[accessor] ?? ''} placement="right" arrow>
			<Box>{content}</Box>
		</Tooltip>
	) : (
		content
	);
};

NavItem.propTypes = {
	item: PropTypes.object,
	level: PropTypes.number,
	parentPath: PropTypes.string || null,
	selectionVariant: PropTypes.oneOf(['brand', 'dark']),
	showIcon: PropTypes.bool,
};

export default NavItem;
